import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { type LucideIcon } from 'lucide-react';
import { Button, type ButtonProps } from '../../../components/ui/button';
import { skillAvailability, useAiCapabilities } from '../use-ai-capabilities';
import { AiKeyRequiredDialog } from './ai-key-required';
import { AiRunConfirmDialog } from './ai-run-confirm';

interface AiActionProps {
  /** Tên skill, khớp `AiSkill.name` phía backend. */
  readonly skill: string;
  readonly label: string;
  readonly onRun: () => void;
  readonly pending?: boolean;
  /** Tham số chưa đủ để chạy — nút tắt, thay vì mở hộp xác nhận rồi không làm gì. */
  readonly disabled?: boolean;
  /**
   * Hỏi "Thực thi / Huỷ" trước khi gọi `onRun` — mặc định **có**.
   *
   * ⚠️ Chỉ tắt khi `onRun` **không** chạy AI, mà chỉ mở ra một màn hình có nút
   * AI của riêng nó (ví dụ `PlanDraftsButton` mở hộp kế hoạch nháp). Hỏi ở cả
   * hai tầng là hỏi hai lần cho một lượt chạy.
   */
  readonly confirm?: boolean;
  /** Tham số thay vào `$paramName` trong route làm tay. */
  readonly routeParams?: Record<string, string>;
  /**
   * Cỡ và kiểu nút — mặc định để trống thì `Button` tự lấy `default`/`default`,
   * đúng hành vi cũ. Nơi đặt nút này cạnh những nút khác (thanh công cụ) truyền
   * vào để cả hàng cùng cỡ, thay vì mỗi nút một chiều cao — APM-UX-ai6.
   */
  readonly size?: ButtonProps['size'];
  readonly variant?: ButtonProps['variant'];
  /**
   * Icon đứng trước nhãn — màu theo chữ của nút (`currentColor`), không tô
   * riêng. Bốn nút cùng thanh công cụ có nút không hề có icon màu (Sửa biểu
   * đồ, Việc mới); tô riêng icon của hai nút AI thì bốn nút lại lệch nhau ở
   * đúng chỗ đang cố gộp — APM-UX-ai6.
   */
  readonly icon?: LucideIcon;
  /** Class thêm cho nút khi AI đã bật — ví dụ để khớp hover với các nút cạnh nó. */
  readonly className?: string;
  /**
   * Hiện lối đi làm tay ngay tại chỗ khi AI chưa bật — mặc định **không**.
   *
   * Mặc định là im lặng vì `AiAction` nằm rải rác trong các màn hình làm việc
   * (thanh công cụ Công việc, thanh Replay), và ở đó câu "bạn chưa nhập key
   * AI… hoặc làm thủ công…" hiện lên năm chỗ cùng lúc trong lúc người dùng
   * đang làm việc khác. Đường làm tay ở những chỗ ấy **chính là giao diện đang
   * mở** — nút "Việc mới" nằm ngay cạnh — nên câu nhắc không thêm gì.
   *
   * Việc cấu hình key gom về một chỗ: badge đỏ trên tab Cấu hình
   * (`ProjectSetupStatus`), nơi đếm được và bấm vào sửa được.
   *
   * ⚠️ Bật `showManual` ở những màn hình **nói về chính tính năng AI**
   * (`AiSkillPanel`): ở đó giải thích "chưa cấu hình" là việc của trang, và
   * một trang trống không lời giải thích còn tệ hơn một câu nhắc. Luật #6 vẫn
   * nguyên: `manualAlternative` là trường **bắt buộc** của mọi skill, được
   * kiểm ở server — bản này chỉ đổi chỗ *hiển thị*, không bỏ đường làm tay.
   */
  readonly showManual?: boolean;
}

/**
 * Nút chạy một tính năng AI — hoặc, khi AI chưa bật, lối đi làm tay.
 *
 * Bấm nút **không** chạy ngay: nó mở `AiRunConfirmDialog` nói tính năng làm
 * gì, và chỉ "Thực thi" mới gọi `onRun`. Xem `confirm`.
 *
 * Đây là chỗ ràng buộc gốc của ADR-006 được thực thi ở tầng giao diện: **không
 * màn hình nào tự hỏi "AI có bật không"**. Dùng component này thì nhánh chưa
 * bật AI không thể quên được, vì nó là nhánh mặc định.
 *
 * Chưa nhập key **không phải lỗi** — đó là trạng thái của mọi cài đặt mới. Nên
 * chỗ này không hiện gì màu đỏ: nút vẫn đứng đúng chỗ, bấm vào thì hộp "Cần key
 * AI" chỉ đường (2026-09-25). `showManual` nay chỉ còn tác dụng khi AI đã bật
 * mà người này thiếu quyền dùng skill.
 */
export function AiAction({
  skill,
  label,
  onRun,
  pending = false,
  disabled = false,
  confirm = true,
  routeParams = {},
  size,
  variant,
  icon: Icon,
  className,
  showManual = false,
}: AiActionProps): ReactNode {
  const { t } = useTranslation();
  const { data, isPending } = useAiCapabilities();
  const availability = skillAvailability(skill, data);
  const [confirming, setConfirming] = useState(false);
  const [askingKey, setAskingKey] = useState(false);

  // Trong lúc chờ trả lời thì chưa hiện gì: nhấp nháy một nút AI rồi tắt đi còn
  // khó chịu hơn là hiện chậm một nhịp.
  if (isPending) return null;

  if (availability.available) {
    return (
      <>
        <Button
          type="button"
          size={size}
          variant={variant}
          className={className}
          onClick={() => (confirm ? setConfirming(true) : onRun())}
          disabled={pending || disabled}
        >
          {Icon ? <Icon aria-hidden /> : null}
          {pending ? t('ai.running') : label}
        </Button>
        {confirming ? (
          <AiRunConfirmDialog
            skill={skill}
            fallbackTitle={label}
            onCancel={() => setConfirming(false)}
            onConfirm={() => {
              setConfirming(false);
              onRun();
            }}
          />
        ) : null}
      </>
    );
  }

  /*
    AI chưa bật (chưa có key): nút **vẫn hiện** — người dùng yêu cầu 2026-09-25.

    Trước đây nhánh này trả `null`, nên người mới không bao giờ biết tính năng
    tồn tại và AI tour chiếu sáng vào chỗ trống. Nay nút đứng đúng chỗ, cùng
    hình dạng; bấm vào thì `AiKeyRequiredDialog` chỉ chỗ nhập key và đưa cách
    làm tay. Không chạy gì, không tốn gì.
  */
  if (!data?.enabled) {
    return (
      <>
        <Button
          type="button"
          size={size}
          variant={variant}
          className={className}
          // Không nhận `disabled`: thiếu tham số không phải lý do để giấu câu
          // trả lời "cần key" — người dùng thiếu key trước đã.
          onClick={() => setAskingKey(true)}
        >
          {Icon ? <Icon aria-hidden /> : null}
          {label}
        </Button>
        {askingKey ? (
          <AiKeyRequiredDialog
            skill={skill}
            featureLabel={label}
            routeParams={routeParams}
            onClose={() => setAskingKey(false)}
          />
        ) : null}
      </>
    );
  }

  // AI đã bật nhưng skill vắng mặt = thiếu **quyền**: không bày nút sẽ bị từ chối.
  if (!showManual || !availability.manual) return null;

  const route = availability.manual.route
    ? Object.entries(routeParams).reduce(
        (path, [key, value]) => path.replace(`$${key}`, value),
        availability.manual.route,
      )
    : null;

  return (
    <div className="flex flex-col gap-1 text-sm text-muted-foreground">
      <span>{availability.manual.label}</span>
      {route ? (
        <Link to={route} className="underline underline-offset-2">
          {t('ai.goManual')}
        </Link>
      ) : null}
      {availability.message ? (
        <span className="text-xs opacity-75">{availability.message}</span>
      ) : null}
    </div>
  );
}
